import {Injectable} from '@angular/core';
import {Params} from '@angular/router';
import {environment} from '../../environments/environment';
import {RegisterChannel, RegisterOrigin} from '../models/register-origin.model';

/** Parámetros de campaña que llegan por query string → clave en RegisterOrigin. */
const CAMPAIGN_PARAMS: { [queryParam: string]: keyof RegisterOrigin } = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_content: 'utmContent',
  utm_term: 'utmTerm',
  gclid: 'gclid',
  fbclid: 'fbclid',
  li_fat_id: 'liFatId',
  wa_ref: 'waRef'
};

type CampaignParams = Partial<Record<keyof RegisterOrigin, string>>;

/** Campaña guardada con su fecha de vencimiento (epoch ms). */
interface StoredCampaign {
  data: CampaignParams;
  expiresAt: number;
}

const CAMPAIGN_STORAGE_KEY = 'mc_register_origin';
const FIRST_CONTACT_STORAGE_KEY = 'mc_first_contact';
const MAX_LENGTH = 255;
/** Vigencia de la campaña capturada: 7 días desde la última captura (last-touch). */
const CAMPAIGN_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const VALID_CHANNELS: RegisterChannel[] = ['web', 'android', 'ios'];

/**
 * Captura silenciosa del origen de campaña del registro.
 *
 * - Se captura al entrar a la app (APP_INITIALIZER) y al entrar a /register,
 *   no al enviar el formulario, y se persiste en localStorage durante 7 días
 *   para sobrevivir a la navegación interna y a visitas posteriores.
 * - Criterio last-touch: si la URL trae algún parámetro de campaña, reemplaza
 *   por completo lo guardado; si no trae ninguno, se conserva lo anterior.
 * - Cada valor se trunca a 255 caracteres.
 */
@Injectable({providedIn: 'root'})
export class RegisterOriginService {

  /** Fallback en memoria por si el navegador bloquea el storage. */
  private memoryCampaign: StoredCampaign | null = null;
  private memoryFirstContact: string | null = null;

  /** Captura desde la URL actual del navegador (antes de que el router la modifique). */
  captureFromUrl(search: string = window.location.search): void {
    const params: Params = {};
    new URLSearchParams(search).forEach((value, key) => params[key] = value);
    this.captureFromParams(params);
  }

  /** Captura desde un objeto de query params (p. ej. ActivatedRoute.queryParams). */
  captureFromParams(params: Params): void {
    this.ensureFirstContact();

    const campaign: CampaignParams = {};
    let hasAny = false;
    Object.keys(CAMPAIGN_PARAMS).forEach(queryParam => {
      const value = this.sanitize(params?.[queryParam]);
      if (value !== null) {
        campaign[CAMPAIGN_PARAMS[queryParam]] = value;
        hasAny = true;
      }
    });
    // Last-touch: una visita nueva con campaña pisa la anterior entera
    // (no se mezclan parámetros de dos campañas distintas).
    if (hasAny) {
      this.writeCampaign(campaign);
    }
  }

  /** Arma el objeto a enviar. Siempre devuelve los 11 campos (null si no hay dato). */
  getRegisterOrigin(): RegisterOrigin {
    const campaign = this.readCampaign();
    return {
      utmSource: campaign.utmSource ?? null,
      utmMedium: campaign.utmMedium ?? null,
      utmCampaign: campaign.utmCampaign ?? null,
      utmContent: campaign.utmContent ?? null,
      utmTerm: campaign.utmTerm ?? null,
      gclid: campaign.gclid ?? null,
      fbclid: campaign.fbclid ?? null,
      liFatId: campaign.liFatId ?? null,
      waRef: campaign.waRef ?? null,
      channel: this.getChannel(),
      firstContact: this.ensureFirstContact()
    };
  }

  /**
   * Versión serializada para el backend, que recibe registerOrigin como string.
   * Ej: {"utmSource":"meta","utmMedium":null,...,"channel":"web","firstContact":"2026-09-07T14:22:10Z"}
   */
  getRegisterOriginAsString(): string {
    return JSON.stringify(this.getRegisterOrigin());
  }

  /** Limpia la atribución luego de un alta exitosa, para no reutilizarla en otro registro. */
  clear(): void {
    this.memoryCampaign = null;
    this.memoryFirstContact = null;
    this.removeCampaign();
    this.safeStorage(() => localStorage.removeItem(FIRST_CONTACT_STORAGE_KEY));
  }

  private getChannel(): RegisterChannel {
    const platform = String((environment as any).platform || '').toLowerCase() as RegisterChannel;
    return VALID_CHANNELS.includes(platform) ? platform : 'web';
  }

  /** Devuelve la fecha de primera visita, registrándola si todavía no existe. */
  private ensureFirstContact(): string {
    let firstContact = this.safeStorage(() => localStorage.getItem(FIRST_CONTACT_STORAGE_KEY)) || this.memoryFirstContact;
    if (!firstContact) {
      // ISO 8601 en UTC, sin milisegundos: 2026-09-07T14:22:10Z
      firstContact = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
      this.safeStorage(() => localStorage.setItem(FIRST_CONTACT_STORAGE_KEY, firstContact));
    }
    this.memoryFirstContact = firstContact;
    return firstContact;
  }

  /** Guarda la campaña con vencimiento a 7 días; cada nueva captura con campaña renueva el plazo. */
  private writeCampaign(campaign: CampaignParams): void {
    const stored: StoredCampaign = {data: campaign, expiresAt: Date.now() + CAMPAIGN_TTL_MS};
    this.memoryCampaign = stored;
    this.safeStorage(() => localStorage.setItem(CAMPAIGN_STORAGE_KEY, JSON.stringify(stored)));
    // Limpieza de la versión anterior, que guardaba en sessionStorage
    this.safeStorage(() => sessionStorage.removeItem(CAMPAIGN_STORAGE_KEY));
  }

  /** Devuelve la campaña vigente; si venció, la borra y devuelve vacío. */
  private readCampaign(): CampaignParams {
    const stored = this.readStoredCampaign();
    if (!stored || !(stored.expiresAt > Date.now())) {
      if (stored) {
        this.removeCampaign();
      }
      return {};
    }
    return stored.data || {};
  }

  private readStoredCampaign(): StoredCampaign | null {
    const raw = this.safeStorage(() => localStorage.getItem(CAMPAIGN_STORAGE_KEY));
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && 'expiresAt' in parsed) {
          return parsed as StoredCampaign;
        }
      } catch {
        // valor corrupto: se descarta
      }
      this.safeStorage(() => localStorage.removeItem(CAMPAIGN_STORAGE_KEY));
    }
    return this.memoryCampaign;
  }

  private removeCampaign(): void {
    this.memoryCampaign = null;
    this.safeStorage(() => localStorage.removeItem(CAMPAIGN_STORAGE_KEY));
    this.safeStorage(() => sessionStorage.removeItem(CAMPAIGN_STORAGE_KEY));
  }

  /** Normaliza: toma el primer valor si viene repetido, trimea, vacío → null, trunca a 255. */
  private sanitize(value: unknown): string | null {
    const first = Array.isArray(value) ? value[0] : value;
    if (first === null || first === undefined) {
      return null;
    }
    const str = String(first).trim();
    return str.length ? str.substring(0, MAX_LENGTH) : null;
  }

  private safeStorage<T>(fn: () => T): T | null {
    try {
      return fn();
    } catch {
      return null;
    }
  }
}
